import type { LexicalContent, NodeTypes } from '@mv/core/model/heim/richText';
import { cn } from '@mv/ui-kit/headless';
import { Html } from '@mv/ui-kit/primitives';
import { cva, type VariantProps } from 'class-variance-authority';
import {
	cloneElement,
	type ComponentPropsWithoutRef,
	isValidElement,
	type ReactElement,
	type ReactNode,
	type Ref,
} from 'react';
import { renderBlock } from './blocks/renderBlock';
import { withBlockA11y } from './nodes/blockA11y';
import { LinkNode } from './nodes/LinkNode';
import { ListItem } from './nodes/ListItem';
import { TextNode } from './nodes/TextNode';
import { type PlaceholderReplacements } from './replacePlaceholders';

type HeadingLevel = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

export type RichTextNode = NodeTypes;

export type RichTextContext = {
	replacements: PlaceholderReplacements;
	textClassName?: string;
	size: 'small' | 'regular';
	listType?: 'unordered' | 'ordered';
	listStart?: number;
	/**
	 * A pressable inline link becomes a ClickableSpan, which a screen reader exposes as its own
	 * focus target and prefers over the text around it — so the sentence is never spoken. With a
	 * screen reader active the link is rendered as plain text and offered as an action on its
	 * block instead.
	 */
	screenReaderEnabled?: boolean;
	resolveLink: RichTextLinkResolver;
	renderChildren: (children?: NodeTypes[], context?: Partial<RichTextContext>) => ReactNode;
};

export type RichTextLinkInput = {
	node?: NodeTypes;
	href?: string;
	linkType?: string;
	slug?: string;
	label?: string;
};

export type RichTextLinkResolution = {
	href?: string;
	onPress?: () => void;
	accessibilityLabel?: string;
	target?: '_blank';
	rel?: string;
};

export type RichTextLinkResolver = (input: RichTextLinkInput) => RichTextLinkResolution;

export type RichTextRootProps = Omit<ComponentPropsWithoutRef<typeof Html.Div>, 'children' | 'className'>;

export type RichTextRenderer<TNode extends NodeTypes = NodeTypes> = (
	node: TNode,
	children: ReactNode,
	key: number,
	context: RichTextContext,
) => ReactNode;

export type RichTextRenderers = {
	text?: RichTextRenderer;
	linebreak?: RichTextRenderer;
	paragraph?: RichTextRenderer;
	heading?: RichTextRenderer;
	link?: RichTextRenderer;
	list?: RichTextRenderer;
	listitem?: RichTextRenderer;
	block?: RichTextRenderer;
	unknown?: RichTextRenderer;
};

export type RichTextProps = {
	content?: LexicalContent | null;
	className?: string;
	textClassName?: string;
	replacements?: PlaceholderReplacements;
	size?: 'small' | 'regular';
	screenReaderEnabled?: boolean;
	resolveLink?: RichTextLinkResolver;
	rootProps?: RichTextRootProps;
	rootRef?: Ref<unknown>;
	/**
	 * Attached to the first rendered block. Screen-reader focus has to land on an accessibility
	 * element, and the root is deliberately not one — grouping it would swallow every block.
	 */
	firstBlockRef?: Ref<unknown>;
	renderers?: RichTextRenderers;
};

const headingVariants = cva('my-0 max-w-[20em] py-0 text-highlight', {
	variants: {
		level: {
			h1: 'font-display text-5xl leading-solid font-medium',
			h2: 'font-display text-3xl leading-solid font-medium tracking-display-tight',
			h3: 'font-display text-xl leading-display-xl font-medium tracking-display-tight',
			h4: 'font-display text-lg leading-solid font-medium tracking-display-regular',
			h5: 'font-display text-lg leading-solid font-medium tracking-display-regular',
			h6: 'font-body-bold text-sm leading-solid tracking-display-wide text-secondary uppercase',
		},
	},
});
type HeadingVariantProps = VariantProps<typeof headingVariants>;

export function RichText({
	content,
	className,
	textClassName,
	replacements = {},
	size = 'regular',
	screenReaderEnabled = false,
	resolveLink = defaultResolveLink,
	rootProps,
	rootRef,
	firstBlockRef,
	renderers,
}: RichTextProps): ReactNode {
	if (!content?.root?.children?.length) return null;

	const renderChildren = (children?: NodeTypes[], contextOverrides: Partial<RichTextContext> = {}) =>
		renderNodes(
			children,
			{ replacements, textClassName, resolveLink, size, screenReaderEnabled, renderChildren, ...contextOverrides },
			renderers,
		);

	return (
		<Html.Div
			ref={rootRef as never}
			{...rootProps}
			style={{ flexDirection: 'column', ...(rootProps?.style as Record<string, unknown>) } as never}
			className={cn('rich-text-root flex max-w-full min-w-0 flex-col items-stretch gap-2', className)}
		>
			{withFirstBlockRef(renderChildren(content.root.children), firstBlockRef)}
		</Html.Div>
	);
}

function withFirstBlockRef(children: ReactNode, firstBlockRef: Ref<unknown> | undefined): ReactNode {
	if (!firstBlockRef || !Array.isArray(children)) return children;

	let attached = false;
	return children.map((child) => {
		if (attached || !isValidElement(child)) return child;
		attached = true;
		return cloneElement(child as ReactElement<{ ref?: Ref<unknown> }>, { ref: firstBlockRef });
	});
}

function renderNodes(
	nodes: NodeTypes[] | undefined,
	context: RichTextContext,
	renderers?: RichTextRenderers,
): ReactNode {
	return nodes?.map((node, index) => renderNode(node, index, context, renderers)) ?? null;
}

function renderNode(node: NodeTypes, key: number, context: RichTextContext, renderers?: RichTextRenderers): ReactNode {
	if (node == null) return null;

	const renderer = renderers?.[node.type as keyof RichTextRenderers];
	const listStart = (node as NodeTypes & { start?: unknown }).start;
	const children = context.renderChildren(
		node.children,
		node.type === 'list'
			? {
					listType: node.tag === 'ol' ? 'ordered' : 'unordered',
					listStart: typeof listStart === 'number' ? listStart : 1,
				}
			: context,
	);
	if (renderer) return renderer(node, children, key, context);

	switch (node.type) {
		case 'text':
			return <TextNode key={key} node={node} nodeKey={key} context={context} />;
		case 'linebreak':
			return <Html.Br key={key} />;
		case 'paragraph':
			return withBlockA11y(
				<Html.P
					key={key}
					className={cn(
						'my-0 max-w-[40em] font-body leading-6 font-normal text-primary',
						context.size === 'small' ? 'text-sm' : 'text-base',
						alignmentClass(node.format),
					)}
				>
					{children}
				</Html.P>,
				node.children,
				context,
			);
		case 'heading': {
			const level = isHeadingLevel(node.tag) ? node.tag : 'h2';
			const Heading = Html[level];
			return withBlockA11y(
				<Heading
					key={key}
					className={cn(headingVariants({ level: level as HeadingVariantProps['level'] }), alignmentClass(node.format))}
				>
					{children}
				</Heading>,
				node.children,
				context,
			);
		}
		case 'quote':
			return withBlockA11y(
				<Html.Blockquote key={key} className="border-l-4 border-primary pl-4">
					<Html.Span className="text-primary italic">{children}</Html.Span>
				</Html.Blockquote>,
				node.children,
				context,
			);
		case 'link':
		case 'autolink':
			return (
				<LinkNode key={key} node={node} nodeKey={key} context={context}>
					{children}
				</LinkNode>
			);
		case 'list': {
			const List = node.tag === 'ol' ? Html.Ol : Html.Ul;
			return (
				<List key={key} accessibilityRole="list" className="my-0 flex max-w-[40em] flex-col gap-2 pl-0">
					{children}
				</List>
			);
		}
		case 'listitem':
			return <ListItem key={key} node={node} nodeKey={key} context={context} />;
		case 'block':
			return renderBlock(node, key, context, renderers, children, renderNodes);
		default:
			return renderers?.unknown?.(node, children, key, context) ?? children;
	}
}

function defaultResolveLink({ href }: RichTextLinkInput): RichTextLinkResolution {
	return { href };
}

function isHeadingLevel(value: string | undefined): value is HeadingLevel {
	return value === 'h1' || value === 'h2' || value === 'h3' || value === 'h4' || value === 'h5' || value === 'h6';
}

function alignmentClass(format: unknown) {
	if (typeof format !== 'string') return undefined;

	return {
		left: 'text-left',
		start: 'text-left',
		center: 'text-center',
		right: 'text-right',
		end: 'text-right',
		justify: 'text-justify',
	}[format ?? 'left'];
}
